/**
 * Rasteriza o HTML rico do rodapé institucional (TipTap) para inclusão no PDF via jsPDF,
 * alinhado ao preview em RichHtmlFragment / VisualizarProposta.
 */

/**
 * @typedef {object} RodapeRasterPdf
 * @property {string} dataUrl
 * @property {number} drawWidthMm
 * @property {number} drawHeightMm
 */

import html2canvas from "html2canvas";
import { prepareRichHtmlForDisplay } from "@/lib/sanitizeRichHtml";

/** Altura máxima do bloco raster no PDF (mm); conteúdo maior é reduzido proporcionalmente. */
export const MAX_RODAPE_RASTER_H_MM = 32;

const MM_TO_PX = 96 / 25.4;

function nextFrame() {
  return new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  );
}

/**
 * @param {object} opts
 * @param {string} opts.html — `rodape_texto` da configuração
 * @param {number} opts.contentWidthMm — largura útil entre margens (ex.: pageW - 2*SIDE_MARGIN)
 * @param {number} [opts.scale=2] — escala do html2canvas (nitidez)
 * @returns {Promise<RodapeRasterPdf | null>}
 */
export async function captureRodapeTextoHtmlForPdf({
  html,
  contentWidthMm,
  scale = 2,
}) {
  if (typeof document === "undefined") return null;
  const raw = html != null ? String(html) : "";
  if (!raw.trim()) return null;

  const safe = prepareRichHtmlForDisplay(raw);
  if (!safe || !safe.trim()) return null;

  const contentWidthPx = Math.max(120, Math.round(Number(contentWidthMm) * MM_TO_PX));
  if (!Number.isFinite(contentWidthMm) || contentWidthMm <= 0) return null;

  const root = document.createElement("div");
  root.setAttribute("data-rodape-pdf-raster", "1");
  root.style.cssText = [
    "position:fixed",
    "left:-12000px",
    "top:0",
    `width:${contentWidthPx}px`,
    "box-sizing:border-box",
    "pointer-events:none",
    "z-index:-1",
  ].join(";");

  root.className =
    "rounded-lg bg-blue-50 p-4 text-left dark:bg-slate-900";

  const inner = document.createElement("div");
  inner.className =
    "prose prose-sm max-w-none text-slate-800 text-xs dark:prose-invert dark:text-slate-100 [&_a]:break-all";
  inner.innerHTML = safe;
  root.appendChild(inner);

  try {
    document.body.appendChild(root);
    if (document.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch {
        /* ignore */
      }
    }
    await nextFrame();

    const canvas = await html2canvas(root, {
      scale,
      useCORS: true,
      logging: false,
      backgroundColor: "#f8fafc",
    });

    const cw = canvas.width;
    const ch = canvas.height;
    if (!cw || !ch) return null;

    const aspect = cw / ch;
    let drawW = Number(contentWidthMm);
    let drawH = drawW / aspect;
    if (drawH > MAX_RODAPE_RASTER_H_MM) {
      drawH = MAX_RODAPE_RASTER_H_MM;
      drawW = drawH * aspect;
    }

    return {
      dataUrl: canvas.toDataURL("image/png"),
      drawWidthMm: drawW,
      drawHeightMm: drawH,
    };
  } catch {
    return null;
  } finally {
    root.remove();
  }
}
